import { AccountTradingSnapshot } from 'src/hooks/query/snapshots/account/types';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import { PORTFOLIO_DAYS_BY_PERIOD } from 'src/components/PortfolioPage/consts';

export interface PortfolioFilledTradingSnapshot extends AccountTradingSnapshot {
	/**
	 * Used to determine if specific mocked-snapshot-related UI should be shown.
	 */
	isMockedSnapshot?: boolean;
}

export type PortfolioSnapshotsFillStrategy = 'repeatLastKnown' | 'fillWithZero';

interface Params {
	snapshots: AccountTradingSnapshot[];
	period: PortfolioPeriod;
	strategy: PortfolioSnapshotsFillStrategy;
}

/**
 * For various reasons the BE may not supply a snapshot for every interval.
 * This function fills missing snapshots based on the provided strategy so
 * that there is a data point for every interval in the chart.
 */
export default function fillMissingPortfolioSnapshots({
	snapshots,
	period,
	strategy,
}: Params) {
	if (snapshots.length === 0) {
		return snapshots as PortfolioFilledTradingSnapshot[];
	}

	const sortedSnapshots = [...snapshots].sort((a, b) => a.ts - b.ts);
	const now = Math.floor(Date.now() / 1000);

	const isHourly = period === '24h';
	const intervalCount = isHourly ? 25 : PORTFOLIO_DAYS_BY_PERIOD[period];
	const intervalSeconds = isHourly ? 3600 : 86400;

	// Round down to the nearest interval boundary to include today.
	const endTime = Math.floor(now / intervalSeconds) * intervalSeconds;
	// For N days of daily P&L, we need N+1 snapshots (baseline + N days).
	const startTime = endTime - intervalCount * intervalSeconds;
	const expectedIntervals: number[] = [];

	for (let i = 0; i <= intervalCount; i++) {
		expectedIntervals.push(startTime + i * intervalSeconds);
	}

	const filledSnapshots: PortfolioFilledTradingSnapshot[] = [];
	let lastKnownSnapshot: PortfolioFilledTradingSnapshot | null = null;

	for (const intervalTs of expectedIntervals) {
		const matchingSnapshot = sortedSnapshots.find(
			(snapshot) => Math.abs(snapshot.ts - intervalTs) < intervalSeconds / 2
		);

		if (matchingSnapshot) {
			filledSnapshots.push(matchingSnapshot);
			lastKnownSnapshot = matchingSnapshot;
			continue;
		}

		if (strategy === 'fillWithZero') {
			filledSnapshots.push({
				ts: intervalTs,
				accountBalance: 0,
				unrealizedPnl: 0,
				cumulativeRealizedPnl: 0,
				unsettledPnl: 0,
				cumulativeFunding: 0,
				cumulativeFeePaid: 0,
				cumulativeFeeRebate: 0,
				cumulativeTakerVolume: 0,
				cumulativeMakerVolume: 0,
				cumulativeSettledPnl: 0,
				isMockedSnapshot: true,
			});
			continue;
		}

		if (lastKnownSnapshot) {
			filledSnapshots.push({
				...lastKnownSnapshot,
				ts: intervalTs,
				isMockedSnapshot: true,
			});
			continue;
		}

		const earliestSnapshot = sortedSnapshots[0];
		if (earliestSnapshot && intervalTs <= earliestSnapshot.ts) {
			filledSnapshots.push({
				...earliestSnapshot,
				ts: intervalTs,
				isMockedSnapshot: true,
			});
			lastKnownSnapshot = earliestSnapshot;
		}
	}

	return filledSnapshots;
}
